import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PasienLayout from './PasienLayout';
import axiosInstance from '../../api/axios';
import '../admin/Dashboard.css';
import './pasien-pages.css';
import { Link } from 'react-router-dom';

const normalizeHistory = (item) => {
  const rmData = item.rekam_medis || item.rekamMedis;
  
  return {
    id:             item.id,
    dateDaftar:     item.tanggal_pendaftaran ?? null,
    dateKunjungan:  rmData?.tanggal_kunjungan ?? item.tanggal_pendaftaran ?? null,
    // 🕒 Timestamp lengkap untuk waktu input/pendaftaran/kunjungan
    createdAt:      rmData?.created_at ?? item.created_at ?? item.tanggal_pendaftaran ?? null,
    time:           item.estimasi_jam_kehadiran ?? item.jam_kunjungan?.substring(0, 5) ?? '-',
    doctorId:       item.dokter?.id ?? item.dokter_id ?? 'unknown-doctor',
    doctorName:     item.dokter?.nama ?? 'Dokter Tidak Diketahui',
    specialty:      item.dokter?.spesialisasi ?? 'Umum',
    complaint:      item.keluhan ?? '-',
    status:         item.status ?? '-',
    noAntrian:      item.no_antrian ?? '-',
    tindakan:       rmData?.tindakan ?? null,
    catatan:        rmData?.catatan_dokter ?? null,
    pasien:         item.pasien ?? null,
    resep:          (() => {
      try {
        const raw = rmData?.resep;
        return typeof raw === 'string' ? JSON.parse(raw) : (raw ?? []);
      } catch {
        return [];
      }
    })(),
    hasRekamMedis:  !!rmData,
  };
};

// 🕒 Format Tanggal Saja
const fmtDate = (iso, opts = { day: '2-digit', month: 'long', year: 'numeric' }) =>
  iso ? new Date(iso).toLocaleDateString('id-ID', opts) : '-';

// 🕒 Format Tanggal + Jam Input Presisi
const fmtDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const dateStr = d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeStr = d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }).replace('.', ':');
  return `${dateStr} · ${timeStr} WIB`;
};

// 🕒 Format Jam Saja
const fmtTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }).replace('.', ':') + ' WIB';
};

export default function Riwayat() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  // State Modal Detail Riwayat Dokter
  const [selectedDoctorGroup, setSelectedDoctorGroup] = useState(null);
  const [activeRm, setActiveRm] = useState(null);
  const [activeMobileTab, setActiveMobileTab] = useState('history');

  const fetchHistory = useCallback(async () => {
    setError(null);
    try {
      const res = await axiosInstance.get('/pasien/appointments');
      const raw = res.data?.data ?? res.data ?? [];
      setHistory(raw.map(normalizeHistory));
    } catch (err) {
      console.error('Error fetching history', err);
      setHistory([]);
      setError(err.response?.data?.message ?? 'Gagal memuat riwayat. Silakan coba lagi.');
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchHistory();
      setLoading(false);
    })();
  }, [fetchHistory]);

  // Grouping Data Berdasarkan Dokter Tempat Pasien Berobat
  const groupedDoctors = useMemo(() => {
    const map = new Map();

    history.forEach((rm) => {
      const docId = rm.doctorId;
      const timeStamp = rm.createdAt || rm.dateKunjungan || rm.dateDaftar;

      if (!map.has(docId)) {
        map.set(docId, {
          doctorId: docId,
          doctorName: rm.doctorName,
          specialty: rm.specialty,
          records: [],
          lastVisit: timeStamp,
        });
      }
      
      const group = map.get(docId);
      group.records.push(rm);

      if (new Date(timeStamp) > new Date(group.lastVisit)) {
        group.lastVisit = timeStamp;
      }
    });

    map.forEach((group) => {
      group.records.sort((a, b) => new Date(b.createdAt || b.dateKunjungan) - new Date(a.createdAt || a.dateKunjungan));
    });

    return Array.from(map.values());
  }, [history]);

  // Filter Search
  const filteredDoctors = groupedDoctors.filter((group) => {
    const q = search.toLowerCase();
    if (!q) return true;
    
    const namaDokter = group.doctorName.toLowerCase();
    const spesialis = group.specialty.toLowerCase();
    const hasMatchingComplaint = group.records.some(
      (r) => r.complaint?.toLowerCase().includes(q)
    );

    return namaDokter.includes(q) || spesialis.includes(q) || hasMatchingComplaint;
  });

  const handleOpenDetail = (group) => {
    setSelectedDoctorGroup(group);
    setActiveRm(group.records[0]);
    setActiveMobileTab('history'); 
  };

  return (
    <PasienLayout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        .dx * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

        .dx-header { 
          display: flex; 
          align-items: center; 
          justify-content: space-between; 
          gap: 16px; 
          margin-bottom: 22px; 
          flex-wrap: wrap; 
        }
        .dx-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; }
        .dx-sub   { font-size: 13px; color: #6B7280; margin: 2px 0 0; }

        .dx-search {
          display: flex; align-items: center; gap: 8px; background: #fff;
          border: 1.5px solid #E5E7EB; border-radius: 10px; padding: 9px 14px; 
          width: 100%; max-width: 320px;
        }
        .dx-search input { border: none; outline: none; font-size: 14px; width: 100%; background: transparent; }

        .dx-card { background: #fff; border: 1.5px solid #E5E7EB; border-radius: 14px; overflow: hidden; }
        
        /* Table Default (Desktop) */
        .dx-table-wrapper { width: 100%; overflow-x: auto; }
        .dx-table { width: 100%; border-collapse: collapse; }
        .dx-table thead tr { background: #F9FAFB; border-bottom: 1.5px solid #E5E7EB; }
        .dx-table th { padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 700; color: #6B7280; text-transform: uppercase; }
        .dx-table tbody tr { border-bottom: 1px solid #F3F4F6; cursor: pointer; transition: background .12s; }
        .dx-table tbody tr:hover { background: #F9FAFB; }
        .dx-table td { padding: 14px 16px; font-size: 14px; color: #374151; vertical-align: middle; }

        /* Mobile Cards */
        .dx-mobile-list { display: none; padding: 12px; }
        .doctor-card-mobile {
          background: #fff; border: 1px solid #E5E7EB; border-radius: 10px;
          padding: 14px; margin-bottom: 10px; cursor: pointer; transition: all .15s;
        }
        .doctor-card-mobile:active { background: #F9FAFB; }
        .doctor-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }

        .doctor-name { font-weight: 700; color: #111827; }
        .doctor-meta { font-size: 12px; color: #6B7280; margin-top: 2px; }

        .badge-count {
          display: inline-block; background: #EEF2FF; color: #4338CA;
          font-weight: 700; padding: 3px 10px; border-radius: 20px; font-size: 12px;
        }

        .badge-time {
          display: inline-flex; align-items: center; gap: 4px; background: #F3F4F6; color: #4B5563;
          font-weight: 600; padding: 3px 8px; border-radius: 6px; font-size: 11px; margin-top: 4px;
        }

        .btn-detail {
          padding: 6px 14px; background: #4F46E5; border: none;
          border-radius: 7px; color: #fff; font-size: 12px; font-weight: 700; cursor: pointer;
        }
        .btn-detail:hover { background: #4338CA; }

        .dx-empty { text-align: center; padding: 64px 20px; color: #9CA3AF; }

        /* Modal Styles */
        .modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,.45);
          z-index: 200; display: flex; align-items: center; justify-content: center; padding: 16px;
        }
        .modal-box {
          background: #fff; border-radius: 18px; width: 100%; max-width: 850px;
          max-height: 90vh; overflow: hidden; box-shadow: 0 24px 64px rgba(0,0,0,.18);
          display: flex; flex-direction: column;
        }
        .modal-head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px; border-bottom: 1px solid #E5E7EB; background: #F9FAFB;
        }
        .modal-body-layout { display: flex; flex: 1; overflow: hidden; }

        /* Mobile Modal Tabs */
        .mobile-tab-nav { display: none; background: #F3F4F6; border-bottom: 1px solid #E5E7EB; }
        .mobile-tab-btn {
          flex: 1; padding: 10px; text-align: center; font-size: 13px; font-weight: 600;
          color: #6B7280; border: none; background: transparent; cursor: pointer;
        }
        .mobile-tab-btn.active { color: #4F46E5; background: #fff; border-bottom: 2px solid #4F46E5; }

        /* Sidebar Timeline Kunjungan */
        .history-sidebar {
          width: 270px; background: #F9FAFB; border-right: 1px solid #E5E7EB;
          overflow-y: auto; padding: 16px; flex-shrink: 0;
        }
        .history-title { font-size: 11px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; margin-bottom: 12px; }
        .history-item {
          padding: 10px 12px; border-radius: 8px; border: 1.5px solid #E5E7EB;
          background: #fff; margin-bottom: 8px; cursor: pointer; transition: all .15s;
        }
        .history-item.active { border-color: #6366F1; background: #EEF2FF; }
        .history-date { font-weight: 700; font-size: 13px; color: #111827; }
        .history-time { font-size: 11px; color: #6B7280; margin-top: 2px; }
        .history-complaint { font-size: 12px; color: #6B7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 4px; }

        /* Detail Content */
        .detail-content { flex: 1; overflow-y: auto; padding: 24px; }
        .modal-section { margin-bottom: 18px; }
        .modal-section h5 { font-size: 11px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; margin: 0 0 6px; }
        .modal-section p { font-size: 14px; color: #374151; margin: 0; line-height: 1.5; white-space: pre-wrap; }

        .resep-card { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px; padding: 10px 12px; margin-bottom: 6px; }

        /* RESPONSIVE MOBILE BREAKPOINTS (<= 768px) */
        @media (max-width: 768px) {
          .dx-header { flex-direction: column; align-items: stretch; gap: 12px; }
          .dx-search { max-width: 100%; }

          .dx-table-wrapper { display: none; }
          .dx-mobile-list { display: block; }

          .modal-overlay { padding: 10px; }
          .modal-box { max-height: 95vh; border-radius: 14px; }
          .modal-head { padding: 14px 16px; }
          .modal-head h3 { font-size: 16px !important; }

          .mobile-tab-nav { display: flex; }
          .modal-body-layout { flex-direction: column; }

          .history-sidebar {
            width: 100%;
            border-right: none;
            border-bottom: 1px solid #E5E7EB;
            display: ${activeMobileTab === 'history' ? 'block' : 'none'};
            max-height: 100%;
          }

          .detail-content {
            padding: 16px;
            display: ${activeMobileTab === 'detail' ? 'block' : 'none'};
          }
        }
      `}</style>

      <div className="dx p-4 md:p-6" style={{ width: '100%', boxSizing: 'border-box' }}>
        <div className="dx-header">
          <div>
            <h2 className="dx-title">Riwayat Berobat</h2>
            <p className="dx-sub">Daftar dokter tempat Anda berobat beserta seluruh riwayat kunjungannya</p>
          </div>
          <div className="dx-search">
            <span>🔍</span>
            <input
              placeholder="Cari nama dokter, spesialis, atau keluhan…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div style={{ padding: '12px', borderRadius: '8px', marginBottom: '20px', backgroundColor: '#fee2e2', color: '#991b1b', fontSize: '14px' }}>
            {error}
          </div>
        )}

        <div className="dx-card">
          {loading ? (
            <div className="dx-empty">⏳ Memuat data riwayat…</div>
          ) : filteredDoctors.length === 0 ? (
            <div className="dx-empty">
              📋 Tidak ada data riwayat ditemukan.
              <div style={{ marginTop: 12 }}>
                <Link to="/pasien/daftar-berobat" style={{ color: '#4F46E5', fontWeight: 600 }}>
                  Buat pendaftaran baru →
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* DESKTOP TABLE VIEW */}
              <div className="dx-table-wrapper">
                <table className="dx-table">
                  <thead>
                    <tr>
                      <th>Dokter</th>
                      <th>Kunjungan Terakhir</th>
                      <th>Total Kunjungan</th>
                      <th style={{ textAlign: 'right' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDoctors.map((group, idx) => (
                      <tr key={idx} onClick={() => handleOpenDetail(group)}>
                        <td>
                          <div className="doctor-name">{group.doctorName}</div>
                          <div className="doctor-meta">Spesialis: {group.specialty}</div>
                        </td>
                        <td>
                          <div>{fmtDate(group.lastVisit)}</div>
                          <div className="badge-time">🕒 {fmtTime(group.lastVisit)}</div>
                        </td>
                        <td>
                          <span className="badge-count">{group.records.length} Kali Berobat</span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn-detail">Lihat Riwayat →</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARD VIEW */}
              <div className="dx-mobile-list">
                {filteredDoctors.map((group, idx) => (
                  <div key={idx} className="doctor-card-mobile" onClick={() => handleOpenDetail(group)}>
                    <div className="doctor-card-header">
                      <div>
                        <div className="doctor-name">{group.doctorName}</div>
                        <div className="doctor-meta">Spesialis: {group.specialty}</div>
                      </div>
                      <span className="badge-count">{group.records.length}x Kunjungan</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid #F3F4F6' }}>
                      <span style={{ fontSize: 12, color: '#6B7280' }}>
                        Terakhir: <strong>{fmtDateTime(group.lastVisit)}</strong>
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#4F46E5' }}>Detail →</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── MODAL RIWAYAT DOKTER ── */}
      {selectedDoctorGroup && (
        <div className="modal-overlay" onClick={() => setSelectedDoctorGroup(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            
            {/* Header Modal */}
            <div className="modal-head">
              <div>
                <h3 style={{ margin: 0, fontSize: 18, color: '#111827' }}>
                  {selectedDoctorGroup.doctorName}
                </h3>
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  Spesialis: {selectedDoctorGroup.specialty} · Terakhir Berobat: {fmtDateTime(selectedDoctorGroup.lastVisit)}
                </span>
              </div>
              <button
                style={{ background: '#F3F4F6', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => setSelectedDoctorGroup(null)}
              >
                ✕ Tutup
              </button>
            </div>

            {/* Mobile Tab Navigation */}
            <div className="mobile-tab-nav">
              <button
                className={`mobile-tab-btn ${activeMobileTab === 'history' ? 'active' : ''}`}
                onClick={() => setActiveMobileTab('history')}
              >
                📅 Riwayat ({selectedDoctorGroup.records.length})
              </button>
              <button
                className={`mobile-tab-btn ${activeMobileTab === 'detail' ? 'active' : ''}`}
                onClick={() => setActiveMobileTab('detail')}
              >
                📄 Detail Kunjungan
              </button>
            </div>

            <div className="modal-body-layout">
              {/* Sidebar Timeline Kunjungan Berdasarkan Tanggal & Waktu */}
              <div className="history-sidebar">
                <div className="history-title">Riwayat Kunjungan</div>
                {selectedDoctorGroup.records.map((rm) => {
                  const timestamp = rm.createdAt || rm.dateKunjungan || rm.dateDaftar;
                  return (
                    <div
                      key={rm.id}
                      className={`history-item ${activeRm?.id === rm.id ? 'active' : ''}`}
                      onClick={() => {
                        setActiveRm(rm);
                        setActiveMobileTab('detail');
                      }}
                    >
                      <div className="history-date">📅 {fmtDate(timestamp)}</div>
                      <div className="history-time">🕒 Input: {fmtTime(timestamp)}</div>
                      <div className="history-complaint">
                        {rm.complaint || 'Pemeriksaan'}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Detail Content Rekam Medis */}
              <div className="detail-content">
                {activeRm ? (
                  <div>
                    <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #E5E7EB' }}>
                      <h4 style={{ margin: 0, color: '#111827' }}>
                        Kunjungan ({fmtDate(activeRm.dateKunjungan || activeRm.dateDaftar)})
                      </h4>
                      <div style={{ marginTop: 4, fontSize: 13, color: '#6B7280' }}>
                        🕒 Waktu Pendaftaran/Input: <strong>{fmtDateTime(activeRm.createdAt || activeRm.dateKunjungan || activeRm.dateDaftar)}</strong>
                      </div>
                      <div style={{ marginTop: 2, fontSize: 13, color: '#6B7280' }}>
                        Jam Kehadiran: {activeRm.time} WIB · Status: <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{activeRm.status}</span>
                      </div>
                    </div>

                    <div className="modal-section">
                      <h5>Keluhan Utama</h5>
                      <p>{activeRm.complaint || '-'}</p>
                    </div>

                    {activeRm.tindakan && (
                      <div className="modal-section">
                        <h5>Tindakan Medis</h5>
                        <p>{activeRm.tindakan}</p>
                      </div>
                    )}

                    <div className="modal-section">
                      <h5>Resep Obat</h5>
                      {activeRm.resep && activeRm.resep.length > 0 ? (
                        activeRm.resep.map((o, i) => (
                          <div key={i} className="resep-card">
                            <strong>{o.nama_obat}</strong> — {o.dosis} {o.satuan || ''} ({o.frekuensi} · {o.waktu || '-'})
                          </div>
                        ))
                      ) : (
                        <p>Tidak ada resep obat.</p>
                      )}
                    </div>

                    {activeRm.catatan && (
                      <div className="modal-section">
                        <h5>Catatan Dokter</h5>
                        <p>{activeRm.catatan}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>Pilih riwayat kunjungan dari tab riwayat.</div>
                )}
              </div>
            </div>

          </div>
        </div>
      )}
    </PasienLayout>
  );
}